import { useEffect, useState } from 'react';
import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import {
  setDocument as setReduxDocument,
  setLanguages as setReduxLanguages,
  setLocale as setReduxLocale,
  setReferenceData as setReduxReferenceData,
  setSection as setReduxSection,
  setVersions as setReduxVersions,
} from 'app/state/redux/documentSlice';
import { mapLanguages } from 'app/utils/translations';
import { useEditorDispatch } from 'app/state/contexts/EditorContext';
import { useCss } from 'app/api/style-queries';
import { NO_DRAFT, useDraft as useDesignDraftQuery } from 'app/api/draft-queries';
import { useCreateTemplateStyle } from './document-queries';

export const useDocument = (
  documentId,
  documentRevisionId,
  sectionId,
  sectionRevisionId,
  translationId,
  showErrors = true,
  useDraft = false,
  usingStaticStructure,
  withStyle,
  useReferenceData,
  embedd = false,
  withTheme
) => {
  const [document, setDocument] = useState(null);
  const [section, setSection] = useState(null);
  const editorDispatch = useEditorDispatch();
  const [referenceData, setReferenceData] = useState([]);

  const { data: draftData } = useDesignDraftQuery({ documentSuperId: documentId });
  const draftExists = draftData !== undefined && draftData !== null && draftData !== NO_DRAFT;
  const effectiveUseDraft = useDraft || draftExists;
  const { data: css } = useCss({ documentSuperId: documentId, documentRevisionId, useDraft: effectiveUseDraft, withStyle, withTheme, embedd });
  const { mutate: createTemplateStyle } = useCreateTemplateStyle(documentId);

  useEffect(() => {
    if (usingStaticStructure) {
      const section = {
        content: usingStaticStructure,
      };
      setSection(section);
      editorDispatch(setReduxSection(section));
    }
  }, [usingStaticStructure, editorDispatch]);

  useEffect(() => {
    let isMounted = true;
    fetchDocumentVersions(editorDispatch, documentId)
      .then(() => {
        const queryParams = ['compileSass=true'];
        if (effectiveUseDraft) {
          queryParams.push('useDraft=true');
        }
        if (withStyle) {
          queryParams.push('withStyle=' + withStyle);
        }
        if (withTheme) {
          queryParams.push('withTheme=' + withTheme);
        }

        const query = queryParams.join('&');
        if (documentRevisionId) {
          return request.get(`/gaby/documents/${documentRevisionId}?${query}`).then((res) => res.data);
        } else {
          return request
            .get(`/gaby/documents?superId=${documentId}&current=true&${query}`)
            .then((res) => res.data)
            .then((docs) => {
              if (docs) {
                return docs[docs.length - 1];
              }
            });
        }
      })
      .then(async (doc) => {
        if (!isMounted) return;
        setDocument(doc);
        editorDispatch(setReduxDocument(doc));
        const CHUNK_THRESHOLD = 100;
        const CHUNK_SIZE = 50;
        if (useReferenceData && doc.content.referenceData?.length) {
          const revisionIds = doc.content.referenceData.map((ref) => ref.revisionId);
          if (revisionIds.length === 0) return;

          if (revisionIds.length <= CHUNK_THRESHOLD) {
            const res = await request.post('/gaby/reference-data/batch', {
              revisionIds,
              documentSuperId: documentId,
            });

            if (!isMounted) return;

            setReferenceData(res.data);
            editorDispatch(setReduxReferenceData(res.data));
          } else {
            const chunks = [];
            for (let i = 0; i < revisionIds.length; i += CHUNK_SIZE) {
              chunks.push(revisionIds.slice(i, i + CHUNK_SIZE));
            }

            let allReferenceData = [];

            for (let i = 0; i < chunks.length; i++) {
              const chunkIds = chunks[i];
              const res = await request.post('/gaby/reference-data/batch', {
                revisionIds: chunkIds,
                documentSuperId: documentId,
              });
              allReferenceData = [...allReferenceData, ...res.data];

              if (!isMounted) return;
              setReferenceData(allReferenceData);
              editorDispatch(setReduxReferenceData(allReferenceData));
            }
          }
        }

        if (sectionId) {
          const languages = mapLanguages(doc, sectionId);
          editorDispatch(setReduxLanguages(languages));
          const selectedSectionRevisionId = sectionRevisionId || doc.content.sections.find((section) => section.superId === sectionId).revisionId;

          const translationRevisionId = translationId ? languages.find((lang) => lang.superId === translationId).revisionId : null;
          return request
            .get(`/gaby/sections/${translationRevisionId ? translationRevisionId : selectedSectionRevisionId}`)
            .then((res) => res.data)
            .then((section) => {
              if (!isMounted) return;
              if (section) {
                setSection(section);
                editorDispatch(setReduxSection(section));

                const locale = languages.find((lang) => lang.superId === section.superId).locale;
                editorDispatch(setReduxLocale(locale));
              }
            });
        }
      })
      .catch((e) => {
        if (showErrors) {
          ServerErrorHandler(e);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [documentId, sectionId, documentRevisionId, sectionRevisionId, translationId, withStyle, withTheme, effectiveUseDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTemplateToDocument = (template) => {
    if (document.content.design.find((style) => style.templateSuperId === template.superId)) {
      return;
    }

    createTemplateStyle(
      { template },
      {
        onSuccess: (data) => {
          setDocument(data);
          editorDispatch(setReduxDocument(data));
        },
      }
    );
  };
  return { document, section, css, referenceData, addTemplateToDocument };
};

function fetchDocumentVersions(dispatch, documentId) {
  return request
    .get(`/gaby/versions/${documentId}`)
    .then((res) => res.data)
    .then((versions) => {
      dispatch(setReduxVersions(versions));
    });
}
